import { capitalize, isEmpty } from 'lodash';

import { ConversationJob, ConversationJobProgress } from '@/explore/types';

// Sorting utilities

const sortByCreatedAt = <T extends { createdAt: string }>(a: T, b: T) =>
  a.createdAt.localeCompare(b.createdAt);

const sortByCreatedAtDesc = <T extends { createdAt: string }>(a: T, b: T) =>
  b.createdAt.localeCompare(a.createdAt);

// Progress utilities

const hasMessage = (p: ConversationJobProgress) => !isEmpty(p.statusMessage);

const getLatestProgress = (
  progresses: ConversationJobProgress[],
): ConversationJobProgress | null =>
  progresses.length === 0 ? null : progresses.toSorted(sortByCreatedAtDesc)[0];

const getLatestProgressWithMessage = (
  progresses: ConversationJobProgress[],
): ConversationJobProgress | null => {
  if (progresses.length === 0) {
    return null;
  }
  const sorted = progresses.toSorted(sortByCreatedAtDesc);
  return sorted.find(hasMessage) ?? sorted[0];
};

// Job utilities

export const getJobStatus = (job: ConversationJob) => job.progress?.at(-1)?.status;

export const getJobStatusMessage = (job: ConversationJob) => {
  const lastStatusMessage = job.progress.findLast(hasMessage)?.statusMessage;
  if (lastStatusMessage !== undefined) {
    return lastStatusMessage;
  }
  const lastProgress = job.progress.at(-1);
  if (lastProgress !== undefined) {
    return capitalize(lastProgress.status);
  }
  return undefined;
};

export const getJobStartTime = (job: ConversationJob): number | null => {
  if (job.progress.length === 0) {
    return null;
  }
  const timestamps = job.progress.map((p) => new Date(p.createdAt).getTime());
  return Math.min(...timestamps);
};

export const getJobDuration = (job: ConversationJob): number | null => {
  const startTime = getJobStartTime(job);
  if (startTime === null) {
    return null;
  }
  const timestamps = job.progress.map((p) => new Date(p.createdAt).getTime());
  const endTime = Math.max(...timestamps);
  return Math.round((endTime - startTime) / 1000);
};

export const formatDuration = (seconds: number): string => {
  if (seconds < 60) {
    return `${seconds}s`;
  }
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}m ${remainingSeconds}s`;
};

// Tree types and utilities

export type NestedConversationJob = ConversationJob & {
  children: NestedConversationJob[];
  latestProgress: ConversationJobProgress | null;
};

const computeLatestProgress = (job: NestedConversationJob): ConversationJobProgress | null => {
  const ownProgress = getLatestProgress(job.progress);
  const childProgresses = job.children
    .map(computeLatestProgress)
    .filter((p): p is ConversationJobProgress => p !== null);
  const allProgresses = ownProgress !== null ? [ownProgress, ...childProgresses] : childProgresses;
  return getLatestProgress(allProgresses);
};

const collectAllProgresses = (jobs: NestedConversationJob[]): ConversationJobProgress[] =>
  jobs.flatMap((job) => [...job.progress, ...collectAllProgresses(job.children)]);

const hasStatusInTree =
  (status: string) =>
  (jobs: NestedConversationJob[]): boolean =>
    jobs.some((job) => getJobStatus(job) === status || hasStatusInTree(status)(job.children));

export const buildNestedJobs = (jobs: ConversationJob[]): NestedConversationJob[] => {
  const jobMap = new Map<string, NestedConversationJob>();
  const rootJobs: NestedConversationJob[] = [];

  jobs.forEach((job) => {
    jobMap.set(job.jobId, { ...job, children: [], latestProgress: null });
  });

  jobs.forEach((job) => {
    const nestedJob = jobMap.get(job.jobId)!;
    const hasParent =
      job.parentJobId !== null && job.parentJobId !== undefined && job.parentJobId.length > 0;
    const parent = hasParent ? jobMap.get(job.parentJobId!) : undefined;

    if (parent) {
      parent.children.push(nestedJob);
    } else {
      rootJobs.push(nestedJob);
    }
  });

  const processRecursively = (job: NestedConversationJob): NestedConversationJob => {
    const children = job.children.toSorted(sortByCreatedAt).map(processRecursively);
    const processedJob = { ...job, children };
    return { ...processedJob, latestProgress: computeLatestProgress(processedJob) };
  };

  return rootJobs.toSorted(sortByCreatedAt).map(processRecursively);
};

export type JobTreeStatus = 'running' | 'error' | 'completed';

export const getJobTreeStatus = (jobs: NestedConversationJob[]): JobTreeStatus => {
  if (hasStatusInTree('error')(jobs)) {
    return 'error';
  }
  if (hasStatusInTree('running')(jobs)) {
    return 'running';
  }
  return 'completed';
};

export const getJobTreeStatusMessage = (
  jobs: NestedConversationJob[],
): ConversationJobProgress | null => getLatestProgressWithMessage(collectAllProgresses(jobs));
